import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import useProperties from "../utils/useProperties";
import PropertyCard from "../components/PropertyCard"; // adjust based on your structure
import { motion } from "motion/react"
import searchIcon from '../assets/icons/searchIcon.svg'

const SearchResults = () => {
  const { properties, loading } = useProperties();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const navigate = useNavigate();

  const [filtered, setFiltered] = useState<typeof properties>([]);

  // Extract query parameters
  const locationParam = params.get("location")?.toLowerCase() || "";
  const categoryParam = params.get("category")?.toLowerCase() || "sale";
  const typeParam = params.get("type")?.toLowerCase() || "";
  const bedroomParam = params.get("bedroom") || "";
  const minPriceParam = params.get("minPrice") || "";
  const maxPriceParam = params.get("maxPrice") || "";
  const [priceRange, setPriceRange] = useState("");


  const [location, setLocation] = useState(locationParam);
  const [category, setCategory] = useState(categoryParam);
  const [type, setType] = useState(typeParam);
  const [bedroom, setBedroom] = useState(bedroomParam);
  const [minPrice, setMinPrice] = useState(minPriceParam);
  const [maxPrice, setMaxPrice] = useState(maxPriceParam);

  const [activeTab, setActiveTab] = useState(categoryParam);
  // Update state when URL parameters change
  useEffect(() => {
    setLocation(locationParam);
    setCategory(categoryParam);
    setType(typeParam);
    setBedroom(bedroomParam);
    setMinPrice(minPriceParam);
    setMaxPrice(maxPriceParam);
  }, [locationParam, categoryParam, bedroomParam, minPriceParam, maxPriceParam, typeParam]);

  const parsePriceRange = (rangeStr: string) => {
    switch (rangeStr) {
      case "Below 2m": return { min: 0, max: 2000000 };
      case "2-5m": return { min: 2000000, max: 5000000 };
      case "5-10m": return { min: 5000000, max: 10000000 };
      case "10-above": return { min: 10000000, max: Infinity };
      case "50-100m": return { min: 50000000, max: 100000000 };
      case "100-200m": return { min: 100000000, max: 200000000 };
      case "200-500m": return { min: 200000000, max: 500000000 };
      case "500-above": return { min: 500000000, max: Infinity };
      default: return { min: 0, max: Infinity };
    }
  };
  const handleSearch = () => {
    const { min, max } = parsePriceRange(priceRange);
    const query = new URLSearchParams({
      location,
      category: activeTab,
      type,
      bedroom,
      minPrice: String(min),
      maxPrice: String(max),
    });


    navigate(`/properties/search?${query.toString()}`);
  };

  useEffect(() => {
    if (properties) {
      const results = properties.filter((property) => {
        const locationMatch = property.location.toLowerCase().includes(locationParam);
        const categoryMatch = categoryParam === "" || (
          categoryParam === "rent"
            ? property.category === "Rental"
            : property.category !== "Rental"
        );
        const typeMatch = typeParam === "" || property.type.toLowerCase() === typeParam;
        const bedroomMatch = bedroomParam === "" || property.bed.toString() === bedroomParam || property.bed.toString().toLowerCase() === bedroomParam.toLowerCase();
        const minPriceMatch = Number(property.price) >= Number(minPrice);
        const maxPriceMatch = Number(property.price) <= Number(maxPrice);

        return locationMatch && categoryMatch && bedroomMatch && minPriceMatch && maxPriceMatch && typeMatch;
      });

      setFiltered(results);
    }
  }, [properties, locationParam, categoryParam, bedroomParam, minPriceParam, maxPriceParam, typeParam]);

  if (loading) return <p className="p-6">Loading properties...</p>;

  return (
    <div className="p-6 pt-[70px]">
      <div className='relative'>
        <div className="bg-gray-50 h-[100vh] lg:h-[70vh]">
          <div className="z-10 absolute inset-0 bg-black bg-opacity-40"></div>
          <img src={require('../assets/imgs/propertybg.jpeg')} alt='hero section background' className='block w-full h-full object-cover' />
        </div>
        <div className='top-[50px] md:top-1/2 lg:top-[300px] left-1/2 z-20 absolute flex md:flex-row flex-col items-center gap-[60px] w-[90%] overflow-hidden -translate-x-1/2 md:-translate-y-1/2 md:transform'>
          <motion.div
            initial={{ opacity: 0, scale: 0.8, translateX: 200 }}
            whileInView={{ opacity: 1, scale: 1, translateX: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className='flex flex-col gap-5 m-auto px-7 lg:w-1/2 text-white'>
            <h1 className='font-[PlayfairDisplay] font-semibold text-[40px] md:text-[60px] text-center'>Top Real Estate</h1>
          </motion.div>


          <motion.div
            initial={{ opacity: 0, scale: 0.8, translateY: 400 }}
            animate={{ translateY: 0 }}
            whileInView={{ opacity: 1, scale: 1, translateY: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className='md:w-[40%]'>
            <div className="flex justify-center items-center md:min-h-screen" >
              <div className="bg-white shadow-lg p-6 rounded-lg w-full max-w-md">
                {/* Tabs */}
                <div className="flex border-b">
                  <button
                    className={`w-1/2 py-2 text-center ${activeTab === "sale" ? "text-white bg-[#c08b53]" : "text-gray-800"} font-semibold rounded-t-lg`}
                    onClick={() => setActiveTab("sale")}
                  >
                    FOR SALE
                  </button>
                  <button
                    className={`w-1/2 py-2 text-center ${activeTab === "rent" ? "text-white bg-[#8f9537]" : "text-gray-800"} font-semibold rounded-t-lg`}
                    onClick={() => setActiveTab("rent")}
                  >
                    FOR RENT
                  </button>
                </div>

                {/* Search Form */}
                <div className="space-y-4 mt-4">
                  <div>
                    <label className="text-gray-500 text-sm">Property Location</label>
                    <input
                      type="text"
                      placeholder="Enter an area"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      className="mt-1 p-2 border border-gray-300 rounded w-full"
                    />
                  </div>

                  <div>
                    <label className="text-gray-500 text-sm">I'm interested in ...</label>
                    <select value={type} onChange={(e) => setType(e.target.value)} className="mt-1 p-2 border border-gray-300 rounded w-full">
                      <option value="">Property Categories</option>
                      <option value="duplex">Duplex</option>
                      <option value="terrace">Terrace</option>
                      <option value="bungalow">Bungalow</option>
                      <option value="apartment">Apartment</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-gray-500 text-sm">I'm looking for ...</label>
                    <select value={bedroom} onChange={(e) => setBedroom(e.target.value)} className="mt-1 p-2 border border-gray-300 rounded w-full">
                      <option value="">Bedrooms</option>
                      <option value="1">1 Bedroom</option>
                      <option value="2">2 Bedrooms</option>
                      <option value="3">3 Bedrooms</option>
                      <option value="4">4 Bedrooms</option>
                      <option value="5">5 Bedrooms</option>
                      <option value="Mini flat">Mini flat</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-gray-500 text-sm">I'm interested to pay ...</label>
                    <select value={priceRange} onChange={(e) => setPriceRange(e.target.value)} className="mt-1 p-2 border border-gray-300 rounded w-full">
                      <option value="">Property Price</option>
                      {activeTab === "sale" ? (
                        <>
                          <option value="50-100m">₦50M - ₦100M</option>
                          <option value="100-200m">₦100M - ₦200M</option>
                          <option value="200-500m">₦200M - ₦500M</option>
                          <option value="500-above">₦500M and above</option>
                        </>
                      ) : (
                        <>
                          <option value="Below 2m">Below ₦2M</option>
                          <option value="2-5m">₦2M - ₦5M</option>
                          <option value="5-10m">₦5M - ₦10M</option>
                          <option value="10-above">₦10M and above</option>
                        </>
                      )}
                    </select>
                  </div>

                  <button
                    className="flex justify-center items-center bg-black hover:bg-gray-800 mt-4 p-2 rounded w-full text-white"
                    onClick={handleSearch}
                  >
                    <img src={searchIcon} className='h-[35px]' alt='search icon' /> SEARCH
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      <h2 className="mb-4 font-bold text-xl">Search Results</h2>
      {filtered?.length === 0 ? (
        <p className="text-gray-500">No properties match your filters.</p>
      ) : (
        <div className="gap-6 grid grid-cols-1 md:grid-cols-3">
          {filtered?.map((property) => (
            <Link to={`/properties/${property.category}/${property.id}`}>
              <PropertyCard
                key={property.id}
                title={property.name}
                desc={property.description}
                price={Number(property.price).toLocaleString()}
                imageUrl={`https://akjodem.com/${property.imgs?.[0]}`}
                bedrooms={property.bed}
                bathrooms={property.bath}
                area={property.sqft}
                sold={property.sold}
                featured={property.featured}
              />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchResults;
